import React, { useEffect, useState } from "react";
import { Card, Row, Col, Toast, Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getColors, updateColors, addColors } from "./colorSlice";
import Input from "../../components/input";
import { TaskEdit02Icon } from "../../assets/icons";
import { useLocation } from "react-router-dom";
import CommonButton from "../../components/commonButton";
import { Formik, FieldArray } from "formik";
import CommonFormLabel from "../../components/formLabel";
import { CrossIcon } from "../../assets/icons";

const ColorManagerCard = () => {
  const dispatch = useDispatch();
  const token = localStorage.getItem("admin-auth-token");
  const location = useLocation();

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const selected_organization_id =
    pathSegments.length > 1 && pathSegments[0] === "organization"
      ? pathSegments[1]
      : null;

  const colorsFromRedux = useSelector(
    (state) => state.colors?.colors?.data?.data?.data || []
  );

  const [editingSection, setEditingSection] = useState(false);
  const [editingSectionColors, setEditingSectionColors] = useState([]);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showMessage = (msg, variant) => {
    setSnackbarMessage(msg);
    setSnackbarVariant(variant);
    setShowSnackbar(true);
    setTimeout(() => setShowSnackbar(false), 3000);
  };

  const validateHex = (hex) => {
    if (!hex) return "Required";
    const hexRegex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
    return hexRegex.test(hex) ? undefined : "Invalid hex code";
  };

  useEffect(() => {
    if (!token || !selected_organization_id) return;

    const fetchColors = async () => {
      try {
        const res = await dispatch(
          getColors({
            token,
            organization_id: selected_organization_id,
            page: 1,
            limit: 100,
          })
        );
        if (!res.payload?.data?.success) {
          showMessage(
            res.payload?.data?.message || "Failed to fetch colors",
            "danger"
          );
        }
      } catch (err) {
        console.error(err);
        showMessage("Failed to fetch colors", "danger");
      }
    };

    fetchColors();
  }, [dispatch, token, selected_organization_id]);

  const handleEditClick = () => {
    setEditingSectionColors(colorsFromRedux);
    setEditingSection(true);
  };

  const handleSubmit = async (values) => {
    const slugify = (text) =>
      "--" +
      text
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");

    const allColors = [...values.colors, ...values.newColors];
    const hasInvalidHex = allColors.some((c) => validateHex(c.hex_code));
    if (hasInvalidHex) {
      showMessage("Please fix invalid hex codes", "danger");
      return;
    }

    setIsSubmitting(true);

    try {
      const deletedColorIds = colorsFromRedux
        .filter((original) => !values.colors.find((c) => c.id === original.id))
        .map((c) => c.id);

      const payload = {
        organization_id: selected_organization_id,
        colors: [
          ...values.colors.map((c) => ({
            id: c.id,
            color_name: c.color_name,
            color_slug: slugify(c.color_name),
            hex_code: c.hex_code,
          })),

          ...values.newColors
            .filter((c) => c.color_name && c.hex_code)
            .map((c) => ({
              color_name: c.color_name,
              color_slug: slugify(c.color_name),
              hex_code: c.hex_code,
            })),
        ],
        deleted_ids: deletedColorIds,
      };

      const res = await dispatch(addColors(payload));

      if (!res.payload?.success) {
        showMessage(res.payload?.message || "Failed to save colors", "danger");
        setIsSubmitting(false);
        return;
      }

      const refresh = await dispatch(
        getColors({
          token,
          organization_id: selected_organization_id,
          page: 1,
          limit: 100,
        })
      );

      if (refresh.payload?.success) {
        setEditingSectionColors(refresh.payload.data.data?.data || []);
      }

      showMessage("Colors updated successfully", "success");
      setEditingSection(false);
    } catch (err) {
      console.error(err);
      showMessage("Something went wrong!", "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Toast
        onClose={() => setShowSnackbar(false)}
        show={showSnackbar}
        delay={3000}
        autohide
        bg={snackbarVariant}
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}
      >
        <Toast.Body style={{ color: "white" }}>{snackbarMessage}</Toast.Body>
      </Toast>

      <Container fluid className="px-0">
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Colors</h5>
            {!editingSection && (
              <CommonButton
                icon={
                  <TaskEdit02Icon
                    width={16}
                    height={16}
                    color="#ffffff"
                    strokeWidth="2"
                  />
                }
                title="Edit"
                className="btn-primary primary-shadow"
                onClick={handleEditClick}
              />
            )}
          </Card.Header>

          <Card.Body>
            {editingSection ? (
              <Formik
                initialValues={{ colors: editingSectionColors, newColors: [] }}
                enableReinitialize={true}
                onSubmit={handleSubmit}
              >
                {({ values, setFieldValue, handleSubmit }) => (
                  <form onSubmit={handleSubmit}>
                    <Row className="gy-4">
                      {values.colors.map((color, id) => {
                        const handleRemoveColor = () => {
                          const updatedColors = values.colors.filter(
                            (_, index) => index !== id
                          );
                          setFieldValue("colors", updatedColors);
                        };

                        const hexError = validateHex(color.hex_code);
                        return (
                          <Col
                            md={4}
                            key={`existing-${id}`}
                            className="mb-3 position-relative"
                          >
                            <div className="position-relative p-4">
                              <button
                                type="button"
                                onClick={handleRemoveColor}
                                className="btn position-absolute top-0 end-0 me-2 mt-2"
                                style={{ zIndex: 10 }}
                              >
                                <CrossIcon />
                              </button>

                              <Input
                                id={`color-name-${id}`}
                                name={`colors.${id}.color_name`}
                                placeholder="Color Name"
                                value={color.color_name}
                                disabled
                                className="mb-2"
                              />
                              <Input
                                id={`hex-code-${id}`}
                                name={`colors.${id}.hex_code`}
                                placeholder="Hex Code"
                                value={color.hex_code}
                                onChange={(e) =>
                                  setFieldValue(
                                    `colors.${id}.hex_code`,
                                    e.target.value
                                  )
                                }
                                className={hexError ? "is-invalid" : ""}
                              />
                              {hexError && (
                                <div className="text-danger">{hexError}</div>
                              )}
                            </div>
                          </Col>
                        );
                      })}

                      <FieldArray name="newColors">
                        {({ push, remove }) => (
                          <>
                            {values.newColors.map((color, idx) => {
                              const hexError = validateHex(color.hex_code);
                              return (
                                <Col
                                  md={4}
                                  key={`new-${idx}`}
                                  className="mb-3 p-4"
                                >
                                  <Input
                                    id={`newColors.${idx}.color_name`}
                                    name={`newColors.${idx}.color_name`}
                                    placeholder="Color Name"
                                    value={color.color_name}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `newColors.${idx}.color_name`,
                                        e.target.value
                                      )
                                    }
                                    className="mb-2"
                                  />
                                  <Input
                                    id={`newColors.${idx}.hex_code`}
                                    name={`newColors.${idx}.hex_code`}
                                    placeholder="Hex Code"
                                    value={color.hex_code}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `newColors.${idx}.hex_code`,
                                        e.target.value
                                      )
                                    }
                                    className={hexError ? "is-invalid" : ""}
                                  />
                                  {hexError && (
                                    <div className="text-danger">
                                      {hexError}
                                    </div>
                                  )}
                                  <div className="mt-2">
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm"
                                      onClick={() => remove(idx)}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </Col>
                              );
                            })}

                            <Col md={12} className="mt-3">
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() =>
                                  push({ color_name: "", hex_code: "" })
                                }
                              >
                                Add Color
                              </button>
                            </Col>
                          </>
                        )}
                      </FieldArray>

                      <Col
                        md={12}
                        className="d-flex justify-content-end gap-3 mt-4"
                      >
                        <CommonButton
                          title={isSubmitting ? "Updating..." : "Update"}
                          disabled={isSubmitting}
                          className="btn-primary primary-shadow"
                          type="submit"
                        />
                        <CommonButton
                          title="Cancel"
                          disabled={isSubmitting}
                          className="btn-muted"
                          type="button"
                          onClick={() => setEditingSection(false)}
                        />
                      </Col>
                    </Row>
                  </form>
                )}
              </Formik>
            ) : (
              <Row>
                {colorsFromRedux.map((color) => (
                  <Col md={3} key={color.id} className="mb-3 p-3 text-center">
                    <CommonFormLabel
                      title={`${color.color_name}`}
                      style={{ fontWeight: "bold", fontSize: "16px" }}
                    />
                    <p style={{ fontSize: "14px", color: "#555" }}>
                      {color.hex_code}
                    </p>
                  </Col>
                ))}
              </Row>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default ColorManagerCard;
